/**
 * Admin → Analytics tab.
 *
 * Three responsibilities, on a single screen:
 *   1. Funnel — counts at each acquisition step (signed up → logged in
 *      → drone added → mission created → PDF exported) over a chosen
 *      trailing window (7/30/90 days). Sourced from the dedicated
 *      `/api/admin/analytics/funnel` endpoint.
 *   2. Recent users table with the step each sign-up has reached so the
 *      admin can spot users who got stuck on a given step.
 *   3. Multi-touch reminder pipeline (J+2, J+7, J+14) — daily cron + manual
 *      triggers. Emails are sent in the user's country language (FR/EN/DE/IT/ES).
 *
 * Kept as a standalone component so AdminPage.js stays readable.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, Send, RotateCw, Mail, Clock, UserMinus, Eye } from 'lucide-react';
import { AdminUserJourneyDrawer } from './AdminUserJourneyDrawer';
import { DetailedFunnelPanel } from './DetailedFunnelPanel';
import { SubscriptionClicksPanel } from './SubscriptionClicksPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [
  { value: 7,  label: '7 derniers jours' },
  { value: 30, label: '30 derniers jours' },
  { value: 90, label: '90 derniers jours' },
];
const STEP_LABEL = {
  signed_up:       'Inscrit',
  logged_in:       'Connecté',
  drone_added:     'Drone ajouté',
  mission_created: 'Mission créée',
  pdf_exported:    'PDF exporté',
};
const STEP_COLOR = {
  signed_up:       'bg-slate-100 text-slate-600',
  logged_in:       'bg-amber-100 text-amber-700',
  drone_added:     'bg-blue-100 text-blue-700',
  mission_created: 'bg-emerald-100 text-emerald-700',
  pdf_exported:    'bg-violet-100 text-violet-700',
};
const TOUCH_LABEL = {
  j2:  'J+2 — première relance',
  j7:  'J+7 — découverte produit',
  j14: 'J+14 — appel à feedback',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
};

export function AdminAnalyticsTab({ token }) {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null); // null | 'all' | 'j2' | 'j7' | 'j14'
  const [status, setStatus] = useState(null);
  const [unsub, setUnsub] = useState(null);
  const [journeyUserId, setJourneyUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/analytics/funnel`, {
        params: { period_days: period, limit: 100 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error('Erreur de chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/analytics/reminders-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(r.data);
    } catch (e) { /* status panel is optional */ }
  };

  const loadUnsub = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/analytics/unsubscribes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnsub(r.data);
    } catch (e) { /* unsub panel is optional */ }
  };

  // Re-fetch every time the user switches the trailing window.
  useEffect(() => { load(); loadStatus(); loadUnsub(); }, [period]);

  const runReminders = async (touch) => {
    setSending(touch);
    const labelStr = touch === 'all' ? 'tous les touchpoints (J+2, J+7, J+14)' : TOUCH_LABEL[touch] || touch;
    if (!window.confirm(`Lancer les relances : ${labelStr} ?\n\nChaque utilisateur ne reçoit qu'une relance par touchpoint, dans la langue de son pays (FR/EN/DE/IT/ES).`)) {
      setSending(null);
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/admin/analytics/run-reminders`, {}, {
        params: { touch },
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = r.data.result;
      const sent = res.total_sent ?? res.sent ?? 0;
      toast.success(`${sent} email(s) envoyé(s).`);
      load(); loadStatus();
    } catch (e) {
      toast.error('Échec de l\'envoi des relances');
    } finally {
      setSending(null);
    }
  };

  if (!data) return <div className="text-slate-500">Chargement…</div>;

  const maxCount = Math.max(...data.funnel.map((s) => s.count), 1);

  return (
    <div className="space-y-8" data-testid="admin-analytics-tab">
      {/* ============================================================
       *  Subscription-button click funnel (visitors + signed-in users)
       * ============================================================ */}
      <SubscriptionClicksPanel token={token} />

      {/* ============================================================
       *  Phase 1 — enriched product funnel (event-based drop-off view)
       * ============================================================ */}
      <DetailedFunnelPanel token={token} />

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-sky-600" />
          <h2 className="text-lg font-bold text-slate-900 font-[Barlow]">Funnel de conversion</h2>
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm focus:ring-sky-500 focus:border-sky-500"
            data-testid="analytics-period-select"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <button
            onClick={() => { load(); loadStatus(); loadUnsub(); }}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 h-9 rounded-md text-sm border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            data-testid="analytics-refresh"
          >
            <RotateCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        </div>
      </div>

      {/* Funnel bar chart */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="space-y-3">
          {data.funnel.map((step, idx) => {
            const width = step.count ? Math.max(8, (step.count / maxCount) * 100) : 8;
            return (
              <div key={step.step} className="flex items-center gap-4">
                <div className="w-44 text-sm font-medium text-slate-700">{step.step}</div>
                <div className="flex-1 bg-slate-100 rounded-md h-9 relative overflow-hidden">
                  <div
                    className="h-9 bg-gradient-to-r from-sky-500 to-sky-400 rounded-md transition-all"
                    style={{ width: `${width}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-3 text-sm font-semibold text-slate-900">
                    {step.count} <span className="ml-2 text-slate-500 font-normal">({step.pct}%)</span>
                  </div>
                </div>
                {idx > 0 && (
                  <div className="w-20 text-right text-xs text-slate-500">
                    {data.funnel[idx - 1].count > 0
                      ? `${Math.round(100 * step.count / data.funnel[idx - 1].count)}% vs ↑`
                      : '—'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* GDPR unsubscribes */}
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="unsubscribes-panel">
        <div className="flex items-center gap-2 mb-1">
          <UserMinus className="w-5 h-5 text-rose-600" />
          <h3 className="text-base font-bold text-slate-900 font-[Barlow]">Désinscriptions emails</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Utilisateurs ayant cliqué sur le lien &laquo;&nbsp;Se désabonner&nbsp;&raquo; dans une relance. Le compte reste actif, seules les relances J+2/J+7/J+14 sont bloquées.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="border border-rose-200 bg-rose-50 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-rose-700 mb-1">Total</div>
            <div className="text-2xl font-bold text-rose-900" data-testid="unsub-total">{unsub?.total_unsubscribed ?? '—'}</div>
            <div className="text-xs text-rose-700 mt-1">{unsub?.rate_pct != null ? `${unsub.rate_pct}% des inscrits` : ''}</div>
          </div>
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-slate-500 mb-1">7 derniers jours</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="unsub-7d">{unsub?.last_7d ?? '—'}</div>
          </div>
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-slate-500 mb-1">30 derniers jours</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="unsub-30d">{unsub?.last_30d ?? '—'}</div>
          </div>
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Base utilisateurs</div>
            <div className="text-2xl font-bold text-slate-900">{unsub?.total_users ?? '—'}</div>
          </div>
        </div>

        {unsub?.recent?.length > 0 ? (
          <div className="border-t border-slate-100 pt-4">
            <div className="text-xs uppercase font-semibold text-slate-500 mb-2">Désinscriptions récentes</div>
            <ul className="space-y-1.5 text-sm" data-testid="unsub-recent-list">
              {unsub.recent.map((r) => (
                <li key={r.email + r.unsubscribed_at} className="flex items-center justify-between border border-slate-100 rounded-md px-3 py-2">
                  <span className="font-mono text-slate-700">{r.email}</span>
                  <span className="text-xs text-slate-500">{fmtDate(r.unsubscribed_at)}{r.country ? ` · ${r.country.toUpperCase()}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            Aucune désinscription enregistrée. 🎉
          </div>
        )}
      </div>

      {/* Reminder pipeline */}
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="reminder-pipeline">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-5 h-5 text-sky-600" />
          <h3 className="text-base font-bold text-slate-900 font-[Barlow]">Pipeline de relances</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Emails envoyés dans la langue du pays de l&apos;utilisateur (FR/EN/DE/IT/ES). Chaque touchpoint est idempotent : 1 envoi maximum par utilisateur et par étape.
        </p>

        {status?.next_cron_run && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-sky-50 border border-sky-200 rounded-md text-sm text-sky-900">
            <Clock className="w-4 h-4" />
            <span>Prochain envoi automatique : <strong>{fmtDate(status.next_cron_run)}</strong></span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {['j2', 'j7', 'j14'].map((t) => (
            <div key={t} className="border border-slate-200 rounded-lg p-4">
              <div className="text-xs uppercase font-semibold text-slate-500 mb-1">{TOUCH_LABEL[t]}</div>
              <div className="text-2xl font-bold text-slate-900 mb-2">{status?.sent_counts?.[t] ?? '—'}</div>
              <div className="text-xs text-slate-500 mb-3">emails envoyés au total</div>
              <button
                onClick={() => runReminders(t)}
                disabled={sending !== null}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 h-8 rounded-md text-xs font-medium border border-sky-300 text-sky-700 bg-white hover:bg-sky-50 disabled:opacity-60"
                data-testid={`run-reminder-${t}`}
              >
                <Send className="w-3.5 h-3.5" />
                {sending === t ? 'Envoi…' : 'Lancer'}
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={() => runReminders('all')}
          disabled={sending !== null}
          className="w-full inline-flex items-center justify-center gap-2 px-4 h-10 rounded-md text-sm font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60"
          data-testid="run-reminder-all"
        >
          <Send className="w-4 h-4" />
          {sending === 'all' ? 'Envoi en cours…' : 'Lancer tout le pipeline maintenant (J+2 + J+7 + J+14)'}
        </button>
      </div>

      {/* Recent users table */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-base font-bold text-slate-900 mb-4 font-[Barlow]">
          Derniers inscrits — {data.recent_users.length} utilisateurs
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 pr-3 font-medium text-slate-500">Inscrit le</th>
                <th className="py-2 pr-3 font-medium text-slate-500">Email</th>
                <th className="py-2 pr-3 font-medium text-slate-500">Pays</th>
                <th className="py-2 pr-3 font-medium text-slate-500">Étape actuelle</th>
                <th className="py-2 pr-3 font-medium text-slate-500">Dernière connexion</th>
                <th className="py-2 pr-3 font-medium text-slate-500">Relances</th>
                <th className="py-2 pr-3 font-medium text-slate-500 text-right">Parcours</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_users.map((u) => {
                const rs = u.reminders_sent || {};
                return (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                      {(u.created_at || '').slice(0, 10)}
                    </td>
                    <td className="py-2 pr-3 text-slate-800">{u.email}</td>
                    <td className="py-2 pr-3 text-slate-600 uppercase">{u.country || '—'}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STEP_COLOR[u.step] || 'bg-slate-100'}`}>
                        {STEP_LABEL[u.step] || u.step}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                      {u.last_login ? u.last_login.slice(0, 10) : <span className="text-slate-400">jamais</span>}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <div className="flex gap-1">
                        {['j2', 'j7', 'j14'].map((t) => {
                          const v = rs[t];
                          const sent = v && v !== 'SKIPPED_HAS_MISSION';
                          const skipped = v === 'SKIPPED_HAS_MISSION';
                          return (
                            <span
                              key={t}
                              title={sent ? `${t.toUpperCase()} envoyé le ${v?.slice(0, 10)}` : skipped ? `${t.toUpperCase()} ignoré (mission déjà créée)` : `${t.toUpperCase()} non envoyé`}
                              className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                                sent ? 'bg-emerald-100 text-emerald-700'
                                  : skipped ? 'bg-slate-100 text-slate-500'
                                  : 'bg-slate-50 text-slate-300'
                              }`}
                            >
                              {t.toUpperCase()}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <button
                        type="button"
                        onClick={() => setJourneyUserId(u.id)}
                        className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-xs font-medium border border-sky-300 text-sky-700 bg-white hover:bg-sky-50"
                        data-testid={`view-journey-${u.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Voir parcours
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* User journey drawer */}
      {journeyUserId && (
        <AdminUserJourneyDrawer
          userId={journeyUserId}
          token={token}
          onClose={() => setJourneyUserId(null)}
        />
      )}
    </div>
  );
}
